import { useState } from 'react';
import { CalendarDays, Loader2, X } from 'lucide-react';
import {
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_LABELS,
  BADGE_UNIVERSOS,
  isSingleEventActivity,
  type ActivityType,
  type BadgeUniverso,
} from 'shared';
import {
  useCreateActivity,
  useUpdateActivity,
  type Activity,
  type ActivityCreated,
} from '@/hooks/useActivities';
import { useBadgesDropdown } from '@/hooks/useBadges';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { ApiValidationError } from '@/lib/api';
import {
  TOO_MANY_SESSIONS_MESSAGE,
  emptySessionForm,
  sessionServerErrors,
  toSessionCreateInput,
  tooManySessions,
  validateSessionForm,
  type SessionForm,
  type SessionFormErrors,
} from '@/lib/activityTime';
import { useAuthStore } from '@/stores/auth';
import { FormField } from './ActivityUi';
import SessionFields from './SessionFields';
import { useStaffOptions } from './useStaffOptions';

const MAX_NAME = 200;
const MAX_TEXT = 5000;

interface ActivityForm {
  name: string;
  type: ActivityType;
  universo: BadgeUniverso | '';
  digitalCenterId: number | null;
  ownerUserId: number | null;
  badgeId: string;
  description: string;
  syllabus: string;
  tools: string;
}

type FormErrors = Partial<Record<keyof ActivityForm, string>>;

function validate(form: ActivityForm): FormErrors {
  const e: FormErrors = {};
  if (!form.name.trim()) e.name = 'O nome da atividade é obrigatório.';
  else if (form.name.trim().length > MAX_NAME) e.name = `Máximo de ${MAX_NAME} caracteres.`;
  if (form.digitalCenterId === null) e.digitalCenterId = 'O Centro Digital é obrigatório.';
  if (form.description.length > MAX_TEXT) e.description = `Máximo de ${MAX_TEXT} caracteres.`;
  if (form.syllabus.length > MAX_TEXT) e.syllabus = `Máximo de ${MAX_TEXT} caracteres.`;
  if (form.tools.length > MAX_TEXT) e.tools = `Máximo de ${MAX_TEXT} caracteres.`;
  return e;
}

interface ActivityModalProps {
  mode: 'create' | 'edit';
  initial?: Activity;
  onClose: () => void;
  /**
   * `sessions` are those created with the activity: the one that is an open
   * day, a Circuito's first visit, several for a club that repeats, none on
   * an edit.
   */
  onSuccess: (msg: string, activity: Activity, sessions: ActivityCreated['sessions']) => void;
  onError: (msg: string) => void;
}

export default function ActivityModal({
  mode,
  initial,
  onClose,
  onSuccess,
  onError,
}: ActivityModalProps) {
  const create = useCreateActivity();
  const update = useUpdateActivity();
  const currentUser = useAuthStore((s) => s.user);

  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];
  const badgesQuery = useBadgesDropdown();
  const staff = useStaffOptions();

  const [form, setForm] = useState<ActivityForm>({
    name: initial?.name ?? '',
    type: initial?.type ?? 'club',
    universo: initial?.universo ?? '',
    digitalCenterId: initial?.digitalCenterId ?? null,
    ownerUserId: initial?.ownerUserId ?? currentUser?.id ?? null,
    badgeId: initial?.badgeId ?? '',
    description: initial?.description ?? '',
    syllabus: initial?.syllabus ?? '',
    tools: initial?.tools ?? '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverErrors, setServerErrors] = useState<FormErrors>({});

  // When it happens, asked for here so that an activity does not have to be
  // created first and given its session second. An open day is one event and
  // a Circuito starts with a visit, so both have a date; a club may leave its
  // meetings for later.
  const [session, setSession] = useState<SessionForm>(() => emptySessionForm(null));
  const [sessionErrors, setSessionErrors] = useState<SessionFormErrors>({});
  const [scheduleClub, setScheduleClub] = useState(true);
  const isClub = form.type === 'club';
  const isOpenDay = isSingleEventActivity(form.type);
  const withSession = mode === 'create' && (!isClub || scheduleClub);

  // With a single centre there is nothing to choose, so it is chosen.
  const digitalCenterId =
    form.digitalCenterId ?? (centers.length === 1 ? centers[0].id : null);

  // The current owner stays selectable even when the staff list has not
  // loaded or no longer includes them.
  const ownerMissing = !staff.options.some((s) => s.id === form.ownerUserId);

  const isPending = create.isPending || update.isPending;
  const fieldError = (key: keyof ActivityForm) => errors[key] || serverErrors[key];

  const set = <K extends keyof ActivityForm>(key: K, value: ActivityForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (serverErrors[key]) setServerErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const complete: ActivityForm = { ...form, digitalCenterId };
    const clientErrors = validate(complete);
    // Only a club repeats and only an open day lasts several days, whatever
    // the form held while the activity was another type.
    const when: SessionForm = {
      ...session,
      ...(isClub ? {} : { repeat: 'none', repeatUntil: '' }),
      ...(isOpenDay ? {} : { endDate: '' }),
    };
    const whenErrors: SessionFormErrors = !withSession
      ? {}
      : tooManySessions(when)
        ? { repeatUntil: TOO_MANY_SESSIONS_MESSAGE }
        : validateSessionForm(when, isOpenDay);

    setErrors(clientErrors);
    setSessionErrors(whenErrors);
    if (
      Object.keys(clientErrors).length > 0 ||
      Object.keys(whenErrors).length > 0 ||
      complete.digitalCenterId === null
    ) {
      return;
    }

    const data = {
      name: complete.name.trim(),
      type: complete.type,
      universo: complete.universo || null,
      digitalCenterId: complete.digitalCenterId,
      ...(complete.ownerUserId !== null ? { ownerUserId: complete.ownerUserId } : {}),
      badgeId: complete.badgeId || null,
      description: complete.description.trim() || null,
      syllabus: complete.syllabus.trim() || null,
      tools: complete.tools.trim() || null,
    };

    try {
      if (mode === 'create') {
        const created = await create.mutateAsync({
          ...data,
          ...(withSession ? { session: toSessionCreateInput(when) } : {}),
        });
        onSuccess('Atividade criada com sucesso.', created, created.sessions ?? []);
      } else {
        const updated = await update.mutateAsync({ id: initial!.id, data });
        onSuccess('Atividade actualizada com sucesso.', updated, []);
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setServerErrors(err.errors as FormErrors);
        setSessionErrors(sessionServerErrors(err.errors));
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  const invalid = (key: keyof ActivityForm) =>
    fieldError(key) ? 'border-destructive focus:ring-destructive/30' : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="activity-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            <h2 id="activity-modal-title" className="text-lg font-semibold text-foreground">
              {mode === 'create' ? 'Nova Atividade' : 'Editar Atividade'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          id="activity-form"
          onSubmit={handleSubmit}
          noValidate
          className="px-6 py-5 space-y-4 overflow-y-auto"
        >
          <FormField label="Nome" htmlFor="activity-name" required error={fieldError('name')}>
            <input
              id="activity-name"
              className={`input ${invalid('name')}`}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={MAX_NAME}
              placeholder="ex. Clube de Robótica"
              aria-invalid={!!fieldError('name')}
              autoFocus
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Tipo" htmlFor="activity-type" required error={fieldError('type')}>
              <select
                id="activity-type"
                className="input"
                value={form.type}
                onChange={(e) => set('type', e.target.value as ActivityType)}
              >
                {ACTIVITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ACTIVITY_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Universo" htmlFor="activity-universo" error={fieldError('universo')}>
              <select
                id="activity-universo"
                className="input"
                value={form.universo}
                onChange={(e) => set('universo', e.target.value as BadgeUniverso | '')}
              >
                <option value="">Sem universo</option>
                {BADGE_UNIVERSOS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Centro Digital"
              htmlFor="activity-center"
              required
              error={fieldError('digitalCenterId')}
            >
              <select
                id="activity-center"
                className={`input ${invalid('digitalCenterId')}`}
                value={digitalCenterId ?? ''}
                onChange={(e) =>
                  set('digitalCenterId', e.target.value ? Number(e.target.value) : null)
                }
                aria-invalid={!!fieldError('digitalCenterId')}
              >
                <option value="">Escolhe um centro…</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.centerName}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Responsável"
              htmlFor="activity-owner"
              error={fieldError('ownerUserId')}
              hint={
                isOpenDay
                  ? 'Qualquer pessoa da equipa pode dinamizar o Dia Aberto.'
                  : 'Qualquer pessoa da equipa pode dinamizar uma sessão.'
              }
            >
              <select
                id="activity-owner"
                className="input"
                value={form.ownerUserId ?? ''}
                onChange={(e) =>
                  set('ownerUserId', e.target.value ? Number(e.target.value) : null)
                }
              >
                {ownerMissing && form.ownerUserId !== null && (
                  <option value={form.ownerUserId}>
                    {initial?.ownerName ?? currentUser?.name ?? 'Eu'}
                  </option>
                )}
                {staff.options.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          {mode === 'create' && (
            <fieldset className="space-y-4 rounded-lg border border-border p-4">
              <legend className="px-1 text-sm font-medium text-foreground">Quando</legend>

              {isClub && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={scheduleClub}
                    onChange={(e) => setScheduleClub(e.target.checked)}
                  />
                  Marcar já as sessões do clube
                </label>
              )}

              {withSession ? (
                <SessionFields
                  idPrefix="activity-session"
                  form={session}
                  errors={sessionErrors}
                  onChange={(next, touched) => {
                    setSession(next);
                    setSessionErrors((prev) =>
                      touched.reduce((acc, key) => ({ ...acc, [key]: undefined }), prev),
                    );
                  }}
                  allowRepeat={isClub}
                  multiDay={isOpenDay}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  As sessões ficam por marcar. Podes adicioná-las depois, na página do clube.
                </p>
              )}
            </fieldset>
          )}

          <FormField
            label="Credencial"
            htmlFor="activity-badge"
            error={fieldError('badgeId')}
            hint={
              isOpenDay
                ? 'Atribuída a quem esteve presente (opcional).'
                : 'Atribuída a quem esteve presente, a partir de uma sessão (opcional).'
            }
          >
            <select
              id="activity-badge"
              className="input"
              value={form.badgeId}
              onChange={(e) => set('badgeId', e.target.value)}
            >
              <option value="">Sem credencial</option>
              {(badgesQuery.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Descrição"
            htmlFor="activity-description"
            error={fieldError('description')}
          >
            <textarea
              id="activity-description"
              className="input resize-none h-20"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="O que é esta atividade (opcional)."
            />
          </FormField>

          <FormField
            label="Programa"
            htmlFor="activity-syllabus"
            error={fieldError('syllabus')}
            hint={
              isOpenDay
                ? 'Os participantes veem-no quando leem o código (opcional).'
                : 'Os participantes veem-no quando leem o código da sessão (opcional).'
            }
          >
            <textarea
              id="activity-syllabus"
              className="input resize-none h-24"
              value={form.syllabus}
              onChange={(e) => set('syllabus', e.target.value)}
              placeholder="ex. 1.º período: montagem. 2.º período: sensores…"
            />
          </FormField>

          <FormField
            label="Ferramentas"
            htmlFor="activity-tools"
            error={fieldError('tools')}
            hint="O que os participantes vão usar (opcional)."
          >
            <textarea
              id="activity-tools"
              className="input resize-none h-20"
              value={form.tools}
              onChange={(e) => set('tools', e.target.value)}
              placeholder="ex. Kits micro:bit, portáteis, ferro de soldar"
            />
          </FormField>
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="activity-form"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'create' ? 'Criar Atividade' : 'Guardar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
